import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getPricesByCompany,
  createPrice,
  updatePrice,
  deletePrice,
  getAllShippingCompanies,
} from "../../redux/actions";
import { LoadingModal } from "../../components/modal/loading";
import {
  CreatePriceModal,
  EditPriceModal,
  DeleteConfirmModal,
} from "../../components/shipping/ShippingModals";

export const Price = () => {
  const dispatch = useDispatch();
  const { prices, companies, loading } = useSelector((s) => s.shipping);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filterCompanyId, setFilterCompanyId] = useState("");

  useEffect(() => {
    dispatch(getAllShippingCompanies());
  }, [dispatch]);

  useEffect(() => {
    if (filterCompanyId) dispatch(getPricesByCompany(filterCompanyId));
  }, [filterCompanyId, dispatch]);

  const handleCreate = (form) => {
    dispatch(createPrice(form));
    setCreateOpen(false);
  };

  const handleUpdate = (form) => {
    dispatch(updatePrice(selected.id, form));
    setEditOpen(false);
    setSelected(null);
  };

  const handleDelete = () => {
    dispatch(deletePrice(selected.id));
    setDeleteOpen(false);
    setSelected(null);
  };

  const containsColor = {
    REGULAR: "bg-slate-100 text-slate-600",
    LIQUID: "bg-blue-50 text-blue-700",
    POWDER: "bg-yellow-50 text-yellow-700",
    BATTERY: "bg-orange-50 text-orange-700",
  };

  return (
    <div className="space-y-6">
      {loading && <LoadingModal text="Loading..." />}

      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-slate-900">Prices</h1>
        <button
          onClick={() => setCreateOpen(true)}
          className="rounded-xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-600"
        >
          Add Price
        </button>
      </div>

      <div className="flex items-center gap-3">
        <select
          value={filterCompanyId}
          onChange={(e) => setFilterCompanyId(e.target.value)}
          className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-200 bg-white"
        >
          <option value="">Filter by company</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                #
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                Company
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                Rate
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                Unit
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                Contains
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                Shipment
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                Weight Range
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                Status
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {prices.length === 0 ? (
              <tr>
                <td
                  colSpan={9}
                  className="px-4 py-8 text-center text-slate-400 text-sm"
                >
                  {filterCompanyId
                    ? "No prices found for this company."
                    : "Select a company to view prices."}
                </td>
              </tr>
            ) : (
              prices.map((p, i) => (
                <tr key={p.id} className="hover:bg-slate-50 transition">
                  <td className="px-4 py-3 text-slate-500">{i + 1}</td>
                  <td className="px-4 py-3 font-medium text-slate-800">
                    {p.shippingCompany?.name}
                  </td>
                  <td className="px-4 py-3 font-semibold text-slate-800">
                    {p.rate}{" "}
                    <span className="text-xs font-normal text-slate-400">
                      {p.currency}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{p.unit}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-lg px-2 py-1 text-xs font-semibold ${containsColor[p.contains] || "bg-slate-100 text-slate-600"}`}
                    >
                      {p.contains}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {p.shipmentType || "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">
                    {p.minWeight != null && p.maxWeight != null
                      ? `${p.minWeight} – ${p.maxWeight} kg`
                      : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-lg px-2 py-1 text-xs font-semibold ${p.isActive ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-600"}`}
                    >
                      {p.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <button
                      onClick={() => {
                        setSelected(p);
                        setEditOpen(true);
                      }}
                      className="rounded-lg bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-200"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        setSelected(p);
                        setDeleteOpen(true);
                      }}
                      className="rounded-lg bg-rose-50 px-3 py-1 text-xs font-medium text-rose-600 hover:bg-rose-100"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <CreatePriceModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
        companies={companies}
      />
      <EditPriceModal
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setSelected(null);
        }}
        onUpdate={handleUpdate}
        price={selected}
        companies={companies}
      />
      <DeleteConfirmModal
        open={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setSelected(null);
        }}
        onConfirm={handleDelete}
        title="Delete Price"
        message="Are you sure you want to delete this price? Categories using this rate will lose it."
      />
    </div>
  );
};
